import { useState } from "react";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone } from "lucide-react";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", f);
      toast.success("Thank you — we'll be in touch soon.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  }
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <section className="py-24">
      <SEO title="Contact" description="Reach the HOFA team in Liberia or Vermont. Questions about programs, partnerships, or volunteering — we'd love to hear from you." path="/contact" />
      <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.1fr_.9fr] gap-12">
        <div>
          <Eyebrow>Contact</Eyebrow>
          <h1 className="text-[clamp(34px,4.5vw,52px)] mb-4 max-w-[24ch] font-display font-bold">
            Reach the HOFA team.
          </h1>
          <p className="text-ink/75 max-w-[54ch] mb-10">
            Questions about programs, partnerships, or volunteering? Send a note and we'll route it to the right team in Liberia or Vermont.
          </p>

          <form onSubmit={submit} className="bg-white border border-ink/10 rounded-[14px] p-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="contact-name" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
              <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="contact-email" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
            </div>
            <input placeholder="Subject" value={f.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
            <textarea required rows={5} placeholder="Your message" value={f.message} onChange={set("message")} data-testid="contact-message" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
            <button disabled={loading} data-testid="contact-submit-btn" className="mt-5 bg-palm text-white font-bold rounded-full px-6 py-3.5 hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60">
              {loading ? "Sending…" : "Send message →"}
            </button>
          </form>
        </div>

        <aside className="space-y-5">
          <div className="rounded-[14px] p-7 text-white" style={{ background: "linear-gradient(160deg,#123B23,#1F5C38)" }}>
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-mango">Liberia HQ</span>
            <h3 className="font-display text-2xl mt-2 mb-3">Mango Town, Brewerville</h3>
            <p className="text-white/80 flex gap-2 items-start mb-2"><MapPin size={16} className="mt-1 shrink-0" /> Montserrado County · District 17</p>
            <p className="text-white/80 flex gap-2 items-center"><Phone size={16} /> +231 770 758 626</p>
            <p className="text-white/80 flex gap-2 items-center mt-1"><Phone size={16} /> +231 77 659 5045</p>
          </div>
          <div className="rounded-[14px] p-7 bg-white border border-ink/10">
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-clay">USA Office</span>
            <h3 className="font-display text-2xl mt-2 mb-3">Essex Junction, Vermont</h3>
            <p className="text-ink/75 flex gap-2 items-start mb-2"><MapPin size={16} className="mt-1 shrink-0" /> United States of America</p>
            <p className="text-ink/75 flex gap-2 items-center"><Phone size={16} /> +1 603 728 9072</p>
          </div>
        </aside>
      </div>
    </section>
  );
}
